import { useMemo } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { useCachedFilterFields } from "components/Filters/hooks";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import { SortOption } from "components/Filters/types";
import { SEARCH_STACK_SUGGESTIONS_DICTIONARY } from "constants/stack";
import { SEARCH_OPEN_TOFU_MIGRATE_SUGGESTIONS } from "shared/Stack/OpenTofuMigration/gql";

import { READY_TO_MIGRATE_FILTER_VARIABLES } from "./constants";

const SORT_LABELS: Record<string, string | undefined> = SEARCH_STACK_SUGGESTIONS_DICTIONARY;

const useOpenTofuMigrationSearchReadySuggestions = () => {
  const { filterVariables } = useFiltersVariables(READY_TO_MIGRATE_FILTER_VARIABLES);
  const { searchInput, predicates } = filterVariables;

  const { data, loading, error, previousData } = usePolledQuery(
    SEARCH_OPEN_TOFU_MIGRATE_SUGGESTIONS,
    {
      variables: {
        input: {
          fields: null,
          predicates,
          fullTextSearch: searchInput,
        },
      },
      pollingGroup: PollingIntervalGroups.Lists,
    }
  );

  const cachedFiltersData = useCachedFilterFields(data?.openTofuMigrateSuggestions?.fields);

  const sortOptions = useMemo(
    (): SortOption[] =>
      cachedFiltersData
        .filter((field) => field.orderable)
        .map((field) => ({
          value: field.name,
          label: SORT_LABELS[field.name] || field.name,
        })),
    [cachedFiltersData]
  );

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: SEARCH_STACK_SUGGESTIONS_DICTIONARY,
  });

  return {
    filters,
    filtersMap: filterOptionsMap,
    sortOptions,
    loading,
    error,
    filteredCount:
      data?.openTofuMigrateSuggestions?.filteredCount ??
      previousData?.openTofuMigrateSuggestions?.filteredCount ??
      0,
  };
};

export default useOpenTofuMigrationSearchReadySuggestions;
